import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

function TradeCardPicker({ cards, selectedIds, onToggle, emptyText }) {
  if (!cards.length) {
    return <p className="no-cards">{emptyText}</p>;
  }

  return (
    <div className="trade-pick-grid">
      {cards.map(card => {
        const selected = selectedIds.includes(card.id);
        return (
          <button
            type="button"
            key={card.id}
            className={`trade-pick ${selected ? 'selected' : ''}`}
            onClick={() => onToggle(card.id)}
            title={card.name}
          >
            {card.jpeg_filename ? (
              <img src={`${API_URL}/cards/${card.jpeg_filename}`} alt={card.name} />
            ) : (
              <span>{card.name}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function TradeMiniCards({ cards }) {
  if (!cards?.length) return <span className="trade-none">Nessuna carta</span>;
  return (
    <div className="trade-mini-grid">
      {cards.map(card => (
        <div key={card.id} className="trade-mini" title={card.name}>
          {card.jpeg_filename && (
            <img src={`${API_URL}/cards/${card.jpeg_filename}`} alt={card.name} />
          )}
        </div>
      ))}
    </div>
  );
}

function Trades({ user, token, socket }) {
  const [partners, setPartners] = useState([]);
  const [myCards, setMyCards] = useState([]);
  const [theirCards, setTheirCards] = useState([]);
  const [trades, setTrades] = useState([]);
  const [filter, setFilter] = useState('incoming');
  const [recipientId, setRecipientId] = useState('');
  const [offerCardIds, setOfferCardIds] = useState([]);
  const [requestCardIds, setRequestCardIds] = useState([]);
  const [offerPatapizze, setOfferPatapizze] = useState('');
  const [requestPatapizze, setRequestPatapizze] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTrades = async () => {
    const res = await axios.get(`${API_URL}/api/trades`, { headers });
    setTrades(res.data);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [partnersRes, myCardsRes] = await Promise.all([
          axios.get(`${API_URL}/api/trades/partners`, { headers }),
          axios.get(`${API_URL}/api/users/${user.id}/cards`),
          fetchTrades()
        ]);
        setPartners(partnersRes.data);
        setMyCards(myCardsRes.data.cards || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Errore nel caricamento degli scambi');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user.id, token]);

  useEffect(() => {
    if (!socket) return;
    const onUpdate = async () => {
      fetchTrades().catch(() => {});
      try {
        const mine = await axios.get(`${API_URL}/api/users/${user.id}/cards`);
        setMyCards(mine.data.cards || []);
      } catch (_) {}
    };
    socket.on('trade_updated', onUpdate);
    return () => socket.off('trade_updated', onUpdate);
  }, [socket, token]);

  useEffect(() => {
    const loadTheirs = async () => {
      setRequestCardIds([]);
      if (!recipientId) {
        setTheirCards([]);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/users/${recipientId}/cards`);
        setTheirCards(res.data.cards || []);
      } catch (err) {
        setTheirCards([]);
        setError(err.response?.data?.error || 'Errore nel caricamento delle carte');
      }
    };
    loadTheirs();
  }, [recipientId]);

  const toggleId = (list, setter, id) => {
    setter(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  };

  const resetForm = () => {
    setRecipientId('');
    setOfferCardIds([]);
    setRequestCardIds([]);
    setOfferPatapizze('');
    setRequestPatapizze('');
  };

  const proposeTrade = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API_URL}/api/trades`, {
        recipient_id: recipientId,
        offer_card_ids: offerCardIds,
        request_card_ids: requestCardIds,
        offer_patapizze: offerPatapizze || 0,
        request_patapizze: requestPatapizze || 0
      }, { headers });
      setSuccess('Scambio proposto!');
      resetForm();
      fetchTrades();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Errore nella proposta di scambio');
    }
  };

  const actOnTrade = async (id, action) => {
    const labels = { accept: 'accettare', reject: 'rifiutare', cancel: 'annullare' };
    if (!window.confirm(`Vuoi ${labels[action]} questo scambio?`)) return;
    setError('');
    try {
      await axios.post(`${API_URL}/api/trades/${id}/${action}`, {}, { headers });
      setSuccess(action === 'accept' ? 'Scambio accettato!' : action === 'reject' ? 'Scambio rifiutato' : 'Scambio annullato');
      fetchTrades();
      const mine = await axios.get(`${API_URL}/api/users/${user.id}/cards`);
      setMyCards(mine.data.cards || []);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Errore nello scambio');
    }
  };

  const incoming = trades.filter(t => t.status === 'pending' && Number(t.recipient_id) === Number(user.id));
  const outgoing = trades.filter(t => t.status === 'pending' && Number(t.proposer_id) === Number(user.id));
  const history = trades.filter(t => t.status !== 'pending');
  const visible = filter === 'incoming' ? incoming : filter === 'outgoing' ? outgoing : history;

  const statusLabel = {
    pending: 'In attesa',
    accepted: 'Accettato',
    rejected: 'Rifiutato',
    cancelled: 'Annullato'
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>Caricamento scambi...</h2>
      </div>
    );
  }

  return (
    <div className="trades-page">
      <h2>🔄 Scambi</h2>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form className="trade-form" onSubmit={proposeTrade}>
        <h3>Nuova proposta</h3>
        <div className="form-group">
          <label>Con chi</label>
          <select value={recipientId} onChange={(e) => setRecipientId(e.target.value)} required>
            <option value="">Seleziona un partecipante</option>
            {partners.map(p => (
              <option key={p.id} value={p.id}>{p.username}</option>
            ))}
          </select>
        </div>

        <div className="trade-sides">
          <div className="trade-side">
            <h4>Tu offri</h4>
            <TradeCardPicker
              cards={myCards}
              selectedIds={offerCardIds}
              onToggle={(id) => toggleId(offerCardIds, setOfferCardIds, id)}
              emptyText="Non hai carte da offrire"
            />
            <div className="form-group">
              <label>Patapizze</label>
              <input
                type="number"
                min="0"
                value={offerPatapizze}
                onChange={(e) => setOfferPatapizze(e.target.value)}
                placeholder="0"
              />
            </div>
          </div>
          <div className="trade-side">
            <h4>Chiedi in cambio</h4>
            <TradeCardPicker
              cards={theirCards}
              selectedIds={requestCardIds}
              onToggle={(id) => toggleId(requestCardIds, setRequestCardIds, id)}
              emptyText={recipientId ? 'Questo utente non ha carte' : 'Seleziona un partecipante'}
            />
            <div className="form-group">
              <label>Patapizze</label>
              <input
                type="number"
                min="0"
                value={requestPatapizze}
                onChange={(e) => setRequestPatapizze(e.target.value)}
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <button type="submit" className="btn btn-primary">Proponi scambio</button>
      </form>

      <div className="filter-buttons">
        <button className={`filter-btn ${filter === 'incoming' ? 'active' : ''}`} onClick={() => setFilter('incoming')}>
          In arrivo ({incoming.length})
        </button>
        <button className={`filter-btn ${filter === 'outgoing' ? 'active' : ''}`} onClick={() => setFilter('outgoing')}>
          Inviati ({outgoing.length})
        </button>
        <button className={`filter-btn ${filter === 'history' ? 'active' : ''}`} onClick={() => setFilter('history')}>
          Cronologia
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="no-cards">Nessuno scambio qui.</div>
      ) : (
        <div className="trade-list">
          {visible.map(trade => {
            const incomingTrade = Number(trade.recipient_id) === Number(user.id);
            return (
              <div key={trade.id} className="trade-card">
                <div className="trade-card-head">
                  <strong>
                    {incomingTrade ? `Da ${trade.proposer_username}` : `A ${trade.recipient_username}`}
                  </strong>
                  <span className={`status-badge badge-${trade.status === 'pending' ? 'scheduled' : trade.status === 'accepted' ? 'active' : 'cancelled'}`}>
                    {statusLabel[trade.status]}
                  </span>
                </div>
                <div className="trade-sides">
                  <div className="trade-side">
                    <h4>{trade.proposer_username} dà</h4>
                    <TradeMiniCards cards={trade.offer_cards} />
                    {trade.offer_patapizze > 0 && (
                      <p><span className="patapizza-icon"></span> {trade.offer_patapizze.toLocaleString()} patapizze</p>
                    )}
                  </div>
                  <div className="trade-side">
                    <h4>{trade.recipient_username} dà</h4>
                    <TradeMiniCards cards={trade.request_cards} />
                    {trade.request_patapizze > 0 && (
                      <p><span className="patapizza-icon"></span> {trade.request_patapizze.toLocaleString()} patapizze</p>
                    )}
                  </div>
                </div>
                {trade.status === 'pending' && (
                  <div className="action-buttons">
                    {incomingTrade ? (
                      <>
                        <button className="btn btn-success btn-sm" onClick={() => actOnTrade(trade.id, 'accept')}>Accetta</button>
                        <button className="btn btn-danger btn-sm" onClick={() => actOnTrade(trade.id, 'reject')}>Rifiuta</button>
                      </>
                    ) : (
                      <button className="btn btn-danger btn-sm" onClick={() => actOnTrade(trade.id, 'cancel')}>Annulla</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Trades;
